// config.json, fetched from the app's base address (03-startup-session.md, Data). Unknown fields
// are ignored; per-server entries may be keyed by base URL or name, trailing slashes don't count.

export interface CompanyEntry {
  /** The company's sign-in service. */
  mas?: string;
  /** The mail server's password check. */
  password?: string;
  mail?: string;
  /** The mail server's host name for other apps (21, "Use desyr mail in other apps"). */
  mailHost?: string;
  /** The company admin service: enables "Forgot password?". */
  admin?: string;
  /** Forces (true) or forbids (false) the sign-in service's own page. */
  masPage?: boolean;
  /** This circle's desyr ID provider at its account service (36, Behaviour 2.2). */
  desyrIdProvider?: string;
  /** The chat server's base URL, used instead of discovery for an entry keyed by server name (03, v1.58.7). */
  chat?: string;
}

/** desyr ID (36, Data): its presence turns 36–39 on. */
export interface DesyrIdConfig {
  issuer: string;
  clientId: string;
  /** The desyr ID's own account page (passkeys, password). */
  account: string;
  /** desyr's admin service for desyr ID calls (`/_desyr/id`). */
  admin: string;
  /** During the move: the old sign-in ways stay reachable. */
  legacy?: boolean;
  /** Case B, "No passkey on this device?" and `/id/device/`, once the capability test passed (36, v1.58.47). */
  deviceLink?: boolean;
}

export interface Config {
  homeserverList?: string[];
  defaultHomeserver?: number;
  desyrSignIn?: Record<string, CompanyEntry>;
  desyrDiscover?: string;
  desyrPush?: Record<string, string>;
  desyrLegal?: { privacy?: string; terms?: string; imprint?: string };
  desyrId?: DesyrIdConfig;
}

export async function loadConfig(): Promise<Config> {
  const answer = await fetch(`${import.meta.env.BASE_URL}config.json`, { cache: 'no-cache' });
  if (!answer.ok) throw Object.assign(new Error('config.json'), { status: answer.status });
  const config = await answer.json();
  if (!config || typeof config !== 'object') throw new Error('config.json is not an object');
  return (current = config as Config);
}

let current: Config = {};
/** config.json as loaded at start. */
export const config = () => current;

/** desyr ID only (39, Behaviour 4): `desyrId` without `legacy`; the circles' old sign-in ways are gone. */
export const idOnly = (c: Config = current) => !!c.desyrId && !c.desyrId.legacy;

const key = (server: string) => server.replace(/\/+$/, '').toLowerCase();

/** A per-server entry, keyed by the server's base URL or its name. */
export function entryFor<T>(map: Record<string, T> | undefined, ...servers: string[]): T | undefined {
  if (!map) return undefined;
  const wanted = new Set(servers.map(key));
  for (const [k, v] of Object.entries(map)) if (wanted.has(key(k))) return v;
  return undefined;
}
